import districtRows from '../../../../../data/bd-districts.json';
import upazilaRows from '../../../../../data/bd-upazilas.json';

/**
 * Bangladesh division → district → upazila, in Bangla, for the address blocks of
 * the printed government forms. Values are the Bangla names the forms print.
 */

// The district file carries a division id but no division names.
const DIVISIONS = [
    { id: '8', bn: 'বরিশাল', en: 'Barishal' },
    { id: '2', bn: 'চট্টগ্রাম', en: 'Chattogram' },
    { id: '3', bn: 'ঢাকা', en: 'Dhaka' },
    { id: '4', bn: 'খুলনা', en: 'Khulna' },
    { id: '1', bn: 'ময়মনসিংহ', en: 'Mymensingh' },
    { id: '7', bn: 'রাজশাহী', en: 'Rajshahi' },
    { id: '6', bn: 'রংপুর', en: 'Rangpur' },
    { id: '5', bn: 'সিলেট', en: 'Sylhet' },
];

// The data file files Kishoreganj under Mymensingh; it belongs to Dhaka division.
const DIVISION_OVERRIDES = { Kishoreganj: '3' };

/** NFC + collapsed spaces, so a typed or restored value still finds its entry. */
const norm = (value) => String(value || '').normalize('NFC').replace(/\s+/g, ' ').trim();

const byBn = (a, b) => a.bn.localeCompare(b.bn, 'bn');

const districts = districtRows
    .map((row) => ({
        id: row.id,
        bn: norm(row.bn_name),
        en: row.name,
        divisionId: DIVISION_OVERRIDES[row.name] || row.division_id,
    }))
    .sort(byBn);

const upazilas = upazilaRows
    .map((row) => ({ id: row.id, bn: norm(row.bn_name), en: row.name, districtId: row.district_id }))
    .sort(byBn);

const divisionByName = (name) => {
    const key = norm(name);
    if (!key) return null;
    const lower = key.toLowerCase();
    return DIVISIONS.find((d) => d.bn === key || d.en.toLowerCase() === lower) || null;
};

export const findDistrict = (name) => {
    const key = norm(name);
    if (!key) return null;
    const lower = key.toLowerCase();
    return districts.find((d) => d.bn === key || d.en.toLowerCase() === lower) || null;
};

const toOption = (item, hint) => ({ label: item.bn, value: item.bn, en: item.en, hint });

export const divisionOptions = () => DIVISIONS.map((d) => toOption(d));

/** Districts of the chosen division; all 64 while no known division is set. */
export const districtOptions = (divisionName) => {
    const division = divisionByName(divisionName);
    return districts
        .filter((d) => !division || d.divisionId === division.id)
        .map((d) => toOption(d));
};

/**
 * Upazilas of the chosen district. With no district yet, every upazila is offered
 * with its district as a hint — picking one fills the district in.
 */
export const upazilaOptions = (districtName) => {
    const district = findDistrict(districtName);
    if (district) return upazilas.filter((u) => u.districtId === district.id).map((u) => toOption(u));
    const districtBn = Object.fromEntries(districts.map((d) => [d.id, d.bn]));
    return upazilas.map((u) => ({ ...toOption(u, districtBn[u.districtId]), value: `${u.bn}|${u.districtId}` }));
};

export const divisionOfDistrict = (districtName) => {
    const district = findDistrict(districtName);
    return district ? DIVISIONS.find((d) => d.id === district.divisionId)?.bn || '' : '';
};

const districtById = (id) => districts.find((d) => d.id === id) || null;

/** Whether an upazila name belongs to the district (unknown names are kept). */
const upazilaFitsDistrict = (upazilaName, districtName) => {
    const district = findDistrict(districtName);
    const key = norm(upazilaName);
    if (!district || !key) return true;
    const matches = upazilas.filter((u) => u.bn === key);
    return !matches.length || matches.some((u) => u.districtId === district.id);
};

/**
 * Wires one address block's division / district / upazila fields together.
 * `keys` names the form fields (`division` is optional — not every form asks).
 *
 * - Picking a division clears a district from another division (and its upazila).
 * - Picking a district fills an empty or mismatched division, and clears an
 *   upazila from another district.
 * - Picking an upazila before a district fills the district (and division).
 *
 * Returns `{ division, district, upazila }` props for GeoSelectField.
 */
export function geoCascade(form, update, keys) {
    const { division: divisionKey, district: districtKey, upazila: upazilaKey } = keys;

    const setDistrict = (name) => {
        update(districtKey, name);
        if (divisionKey) {
            const division = divisionOfDistrict(name);
            if (division && norm(form[divisionKey]) !== division) update(divisionKey, division);
        }
        if (upazilaKey && !upazilaFitsDistrict(form[upazilaKey], name)) update(upazilaKey, '');
    };

    return {
        division: divisionKey ? {
            value: form[divisionKey] || '',
            options: divisionOptions(),
            onChange: (name) => {
                update(divisionKey, name);
                const district = findDistrict(form[districtKey]);
                const division = divisionByName(name);
                if (district && division && district.divisionId !== division.id) {
                    update(districtKey, '');
                    if (upazilaKey) update(upazilaKey, '');
                }
            },
        } : null,
        district: {
            value: form[districtKey] || '',
            options: districtOptions(divisionKey ? form[divisionKey] : ''),
            onChange: setDistrict,
        },
        upazila: upazilaKey ? {
            value: form[upazilaKey] || '',
            options: upazilaOptions(form[districtKey]),
            onChange: (name) => update(upazilaKey, name),
            onSelectOption: (option) => {
                const [, districtId] = String(option.value).split('|');
                const district = districtId ? districtById(districtId) : null;
                if (!district || norm(form[districtKey]) === district.bn) return;
                // Only the parents change: the upazila was just picked, and `form`
                // here still holds the old one, which must not clear the new pick.
                update(districtKey, district.bn);
                if (divisionKey) update(divisionKey, divisionOfDistrict(district.bn));
            },
        } : null,
    };
}
